import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import type { Cell } from "@/data/plans";
import { usePlansCatalog, buildComparison, discountPct } from "@/lib/plansCatalog";

function CellView({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
        <i className="ri-check-line text-sm"></i>
      </span>
    );
  }
  if (value === false) {
    return <span className="text-foreground-300">—</span>;
  }
  return <span className="text-xs font-medium text-foreground-700">{value}</span>;
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { plans } = usePlansCatalog();
  const comparison = buildComparison(plans);

  return (
    <main className="theme-mkt bg-background-50 min-h-screen">
      <Navbar />

      <section className="pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-primary-700 bg-primary-100 rounded-full mb-4">
              Compare Plans
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              Plan comparison at a glance
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Every plan includes unlimited users &amp; terminals and a 7-day free
              trial. Pick the tier that fits — upgrade any time.
            </p>
          </div>

          <div className="overflow-x-auto rounded-lg border border-background-200 shadow-sm">
            <table className="w-full min-w-[640px] border-collapse bg-background-50">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left p-4 md:p-5 text-sm font-semibold text-foreground-600 w-2/5">
                    Features
                  </th>
                  {plans.map((plan, i) => (
                    <th key={plan.tier} className="p-4 md:p-5 text-center">
                      <div
                        className={`text-base md:text-lg font-heading font-bold ${
                          plan.popular ? "text-accent-600" : "text-foreground-950"
                        }`}
                      >
                        {plan.name}
                      </div>
                      <div className="text-xs text-foreground-500 mt-0.5 flex items-center justify-center gap-1.5">
                        <span>{plan.price}<span className="text-foreground-400">{plan.period}</span></span>
                        {discountPct(plan) > 0 && (
                          <span className="text-foreground-400 line-through">{plan.mrp!.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                      <div className="text-[10px] text-foreground-400 mt-0.5">per outlet</div>
                      {discountPct(plan) > 0 && (
                        <div className="mt-1 text-[10px] font-bold text-rose-600">{discountPct(plan)}% OFF</div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-background-100 ${
                      idx % 2 === 1 ? "bg-background-100/40" : ""
                    }`}
                  >
                    <td className="p-4 md:p-5 text-sm text-foreground-800">{row.feature}</td>
                    {row.tiers.map((cell, i) => (
                      <td
                        key={i}
                        className={`p-4 md:p-5 text-center ${
                          i === 1 ? "bg-accent-500/5" : ""
                        }`}
                      >
                        <CellView value={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Start 7-Day Free Trial
            </button>
            <Link
              to="/#pricing"
              className="px-6 py-3 text-sm font-semibold text-foreground-700 border border-background-300 hover:bg-background-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Back to plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
